import { useTranslation } from 'react-i18next';
import {
  AnimatedToggleGroup,
  AnimatedToggleGroupItem,
} from '../ui/animated-toggle-group';
import { leadStyleLabelKey, type LeadClimbStyle } from './lead-climb-style';
import { cn } from '../../lib/utils';

interface LeadStyleSwitchProps {
  /** The styles this climber may climb this route in — `permittedLeadStyles`. */
  styles: LeadClimbStyle[];
  /** What the card says. Null while nobody has chosen, which is not savable. */
  value: LeadClimbStyle | null;
  onChange: (style: LeadClimbStyle) => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Top rope or lead, on the card being written.
 *
 * **Two shapes, because there are two situations.** A class that may do either
 * gets the switch, and it **starts on neither**: there is no sensible default
 * between the two — one is worth more than the other whatever the numbers — so
 * the card is not savable until somebody says which, rather than being born
 * carrying a guess (see `LeadCardEditor`).
 *
 * A class that climbs one way only, or a route forced to one, has nothing to
 * choose: the style is shown as a label so the judge can see what the card is
 * being written as, and the server fills it in whether or not it is sent.
 */
export function LeadStyleSwitch({
  styles,
  value,
  onChange,
  disabled = false,
  className,
  'data-testid': testId,
}: LeadStyleSwitchProps) {
  const { t } = useTranslation();

  if (styles.length === 0) return null;

  if (styles.length === 1) {
    return (
      <span
        className={cn(
          'inline-flex h-9 items-center rounded-lg border bg-muted/50 px-2.5 text-sm text-muted-foreground',
          className
        )}
        data-testid={testId}
      >
        {t(leadStyleLabelKey(styles[0]))}
      </span>
    );
  }

  return (
    <AnimatedToggleGroup
      // Radix reads "" as nothing chosen, which is exactly the state a card
      // starts in on a class that may do either.
      value={value ?? ''}
      onValueChange={(next) => onChange(next as LeadClimbStyle)}
      disabled={disabled}
      fit
      className={cn('w-auto', className)}
      aria-label={t('leadRounds.style.label')}
      data-testid={testId}
    >
      {styles.map((style) => (
        <AnimatedToggleGroupItem key={style} value={style} className="px-3 py-1.5">
          {t(leadStyleLabelKey(style))}
        </AnimatedToggleGroupItem>
      ))}
    </AnimatedToggleGroup>
  );
}
